import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Globe,
  Trophy,
  Calendar,
  Award,
  TrendingUp,
  TrendingDown,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import api from '../../services/api';

// Mock data - will be replaced with API calls
const mockStats = {
  totalUsers: { value: 12847, growth: 12 },
  activeCommunities: { value: 156, growth: 8 },
  ongoingEvents: { value: 23, growth: -3 },
  totalPosts: { value: 0, growth: 0 }
};

// User Growth Chart Data
const userGrowthData = [
  { month: 'Jan', Champions: 2400, Players: 4000 },
  { month: 'Feb', Champions: 3200, Players: 5200 },
  { month: 'Mar', Champions: 4100, Players: 6800 },
  { month: 'Apr', Champions: 5300, Players: 7900 },
  { month: 'May', Champions: 6200, Players: 8500 },
  { month: 'Jun', Champions: 7100, Players: 9200 }
];

// Sport Engagement Data
const sportEngagementData = [
  { name: 'Football', value: 4500, color: '#1e40af' },
  { name: 'Cricket', value: 3200, color: '#eab308' },
  { name: 'Chess', value: 2800, color: '#10b981' },
  { name: 'Basketball', value: 2100, color: '#8b5cf6' },
  { name: 'Tennis', value: 1800, color: '#f59e0b' },
  { name: 'Swimming', value: 1200, color: '#ef4444' }
];

// Bottom stats data - will be replaced with API data
const mockBottomStats = [
  { label: 'Active Sessions', value: '0', trend: 'up', change: '+0%' },
  { label: 'Monthly Growth', value: '+0%', trend: 'up', change: '+0%' },
  { label: 'Avg. Daily Users', value: '0', trend: 'up', change: '+0%' },
  { label: 'Conversion Rate', value: '0%', trend: 'up', change: '+0%' }
];

const mockUpcomingEvents = [
  {
    id: 1,
    name: 'City Chess',
    date: 'Dec 15, 2023',
    participants: '32 participants',
    sport: 'Chess',
    icon: '♟️'
  },
  {
    id: 2,
    name: 'Football League Finals',
    date: 'Dec 18, 2023',
    location: 'Boston',
    sport: 'Football',
    icon: '⚽'
  },
  {
    id: 3,
    name: 'Swimming Gala',
    date: 'Dec 20, 2023',
    participants: '45 participants',
    sport: 'Swimming',
    icon: '🏊'
  }
];

const mockRecentAchievements = [
  {
    id: 1,
    title: 'Regional Tennis',
    winner: 'Sarah Williams',
    timeAgo: '2 days ago',
    sport: 'Tennis',
    icon: '🏆'
  },
  {
    id: 2,
    title: 'Chess Master',
    winner: 'Alex Chen',
    timeAgo: '3 days ago',
    sport: 'Chess',
    icon: '⭐'
  },
  {
    id: 3,
    title: 'Swimming Team',
    winner: 'Emma Davis',
    timeAgo: '4 days ago',
    sport: 'Swimming',
    icon: '🥇'
  }
];

const mockTopCommunities = [
  {
    id: 1,
    name: 'Chess Masters',
    members: 1347,
    sport: 'Chess',
    growth: 12
  },
  {
    id: 2,
    name: 'Football United',
    members: 892,
    sport: 'Football',
    growth: 8
  },
  {
    id: 3,
    name: 'Tennis Academy',
    members: 674,
    sport: 'Tennis',
    growth: 15
  }
];

const mockRecentActivity = [
  {
    id: 1,
    text: 'Sarah Johnson has been verified as a Tennis Coach',
    badge: 'VERIFICATION',
    badgeColor: 'bg-green-100 text-green-800',
    timeAgo: '2 hours ago',
    avatar: 'SJ'
  },
  {
    id: 2,
    text: 'City Chess Championship tournament created',
    badge: 'TOURNAMENT',
    badgeColor: 'bg-blue-100 text-blue-800',
    timeAgo: '5 hours ago',
    avatar: 'CC'
  },
  {
    id: 3,
    text: 'Perfect Scores achievement approved for Mike Chen',
    badge: 'ACHIEVEMENT',
    badgeColor: 'bg-yellow-100 text-yellow-800',
    timeAgo: '12 hours ago',
    avatar: 'MC'
  },
  {
    id: 4,
    text: '12 new users joined the Football community',
    badge: 'USER',
    badgeColor: 'bg-blue-100 text-blue-800',
    timeAgo: '1 day ago',
    avatar: 'FC'
  },
  {
    id: 5,
    text: 'Tennis Captain badge awarded to Emma Davis',
    badge: 'ACHIEVEMENT',
    badgeColor: 'bg-purple-100 text-purple-800',
    timeAgo: '2 days ago',
    avatar: 'ED'
  }
];

// Community Highlights Data
const mockCommunityHighlights = [
  {
    id: 1,
    name: 'Tennis Academy',
    sport: 'Tennis',
    description: 'Professional tennis coaching and matches',
    members: 3216,
    image: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=400&h=200&fit=crop'
  },
  {
    id: 2,
    name: 'Football United',
    sport: 'Football',
    description: 'Competitive football leagues and training',
    members: 2847,
    image: 'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=400&h=200&fit=crop'
  },
  {
    id: 3,
    name: 'Chess Masters',
    sport: 'Chess',
    description: 'Strategic chess tournaments and coaching',
    members: 1923,
    image: 'https://images.unsplash.com/photo-1528819622765-d6bcf132f793?w=400&h=200&fit=crop'
  }
];

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [currentHighlight, setCurrentHighlight] = useState(0);
  const [stats, setStats] = useState(mockStats);
  const [userGrowth, setUserGrowth] = useState(userGrowthData);
  const [sportEngagement, setSportEngagement] = useState(sportEngagementData);
  const [loading, setLoading] = useState(true);
  const [selectedPostRole, setSelectedPostRole] = useState('all');
  const [postsBreakdown, setPostsBreakdown] = useState<any>({});
  const [bottomStats, setBottomStats] = useState(mockBottomStats);
  const [upcomingEvents, setUpcomingEvents] = useState(mockUpcomingEvents);
  const [recentAchievements, setRecentAchievements] = useState(mockRecentAchievements);
  const [topCommunities, setTopCommunities] = useState(mockTopCommunities);
  const [recentActivity, setRecentActivity] = useState(mockRecentActivity);
  const [communityHighlights, setCommunityHighlights] = useState(mockCommunityHighlights);

  // Fetch dashboard data
  React.useEffect(() => {
    fetchDashboardData();
  }, []);

  React.useEffect(() => {
    fetchUserGrowthData();
  }, [selectedPeriod]);

  const fetchDashboardData = async () => {
    setLoading(true);
    console.log('📊 [DASHBOARD] Fetching dashboard stats...');

    try {
      // Fetch all data in parallel so one slow/failing endpoint doesn't block the rest
      const [
        statsResult,
        sportResult,
        postsResult,
        bottomStatsResult,
        eventsResult,
        achievementsResult,
        communitiesResult,
        activityResult,
        highlightsResult
      ] = await Promise.allSettled([
        api.get('/admin/dashboard/stats'),
        api.get('/admin/dashboard/sport-engagement'),
        api.get('/admin/dashboard/posts-by-role'),
        api.get('/admin/dashboard/bottom-stats'),
        api.get('/admin/dashboard/upcoming-events'),
        api.get('/admin/dashboard/recent-achievements'),
        api.get('/admin/dashboard/top-communities'),
        api.get('/admin/dashboard/recent-activity'),
        api.get('/admin/dashboard/community-highlights')
      ]);

      // Apply successful results, keep mock data for failures
      if (statsResult.status === 'fulfilled') {
        console.log('✅ [DASHBOARD] Stats received:', statsResult.value.data.data);
        setStats(statsResult.value.data.data);
      } else {
        console.error('❌ [DASHBOARD] Stats failed:', statsResult.reason);
      }

      if (sportResult.status === 'fulfilled') {
        console.log('✅ [DASHBOARD] Sport engagement received:', sportResult.value.data.data);
        setSportEngagement(sportResult.value.data.data);
      } else {
        console.error('❌ [DASHBOARD] Sport engagement failed:', sportResult.reason);
      }

      if (postsResult.status === 'fulfilled') {
        console.log('✅ [DASHBOARD] Posts breakdown received:', postsResult.value.data.data);
        setPostsBreakdown(postsResult.value.data.data.breakdown || {});
      } else {
        console.error('❌ [DASHBOARD] Posts breakdown failed:', postsResult.reason);
      }

      if (bottomStatsResult.status === 'fulfilled') {
        console.log('✅ [DASHBOARD] Bottom stats received:', bottomStatsResult.value.data.data);
        setBottomStats(bottomStatsResult.value.data.data);
      } else {
        console.error('❌ [DASHBOARD] Bottom stats failed:', bottomStatsResult.reason);
      }

      if (eventsResult.status === 'fulfilled') {
        console.log('✅ [DASHBOARD] Upcoming events received:', eventsResult.value.data.data);
        setUpcomingEvents(eventsResult.value.data.data);
      } else {
        console.error('❌ [DASHBOARD] Upcoming events failed:', eventsResult.reason);
      }

      if (achievementsResult.status === 'fulfilled') {
        console.log('✅ [DASHBOARD] Recent achievements received:', achievementsResult.value.data.data);
        setRecentAchievements(achievementsResult.value.data.data);
      } else {
        console.error('❌ [DASHBOARD] Recent achievements failed:', achievementsResult.reason);
      }

      if (communitiesResult.status === 'fulfilled') {
        console.log('✅ [DASHBOARD] Top communities received:', communitiesResult.value.data.data);
        setTopCommunities(communitiesResult.value.data.data);
      } else {
        console.error('❌ [DASHBOARD] Top communities failed:', communitiesResult.reason);
      }

      if (activityResult.status === 'fulfilled') {
        console.log('✅ [DASHBOARD] Recent activity received:', activityResult.value.data.data);
        setRecentActivity(activityResult.value.data.data);
      } else {
        console.error('❌ [DASHBOARD] Recent activity failed:', activityResult.reason);
      }

      if (highlightsResult.status === 'fulfilled') {
        console.log('✅ [DASHBOARD] Community highlights received:', highlightsResult.value.data.data);
        setCommunityHighlights(highlightsResult.value.data.data);
      } else {
        console.error('❌ [DASHBOARD] Community highlights failed:', highlightsResult.reason);
      }
    } catch (error) {
      console.error('❌ [DASHBOARD] Unexpected error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserGrowthData = async () => {
    try {
      console.log('📈 [DASHBOARD] Fetching user growth data for period:', selectedPeriod);
      const response = await api.get(`/admin/dashboard/user-growth?period=${selectedPeriod}`);
      console.log('✅ [DASHBOARD] User growth data received:', response.data.data);
      setUserGrowth(response.data.data);
    } catch (error) {
      console.error('❌ [DASHBOARD] Failed to fetch user growth data:', error);
      // Keep using mock data if API fails
    }
  };

  const handlePostRoleChange = (role: string) => {
    console.log('📝 [DASHBOARD] Changing post role filter to:', role);
    setSelectedPostRole(role);
  };

  const getPostsCount = () => {
    if (selectedPostRole === 'all') {
      return stats.totalPosts.value;
    }
    const roleMap: any = {
      'player': 'Players',
      'coach': 'Coaches',
      'admin': 'Admins'
    };
    return postsBreakdown[roleMap[selectedPostRole]] || 0;
  };

  const nextHighlight = () => {
    setCurrentHighlight((prev) => (prev + 1) % communityHighlights.length);
  };

  const prevHighlight = () => {
    setCurrentHighlight((prev) => (prev - 1 + communityHighlights.length) % communityHighlights.length);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const StatCard: React.FC<{
    icon: React.ReactNode;
    title: string;
    value: string | number;
    growth: number;
  }> = ({ icon, title, value, growth }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            {icon}
          </div>
          <div>
            <p className="text-sm text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900">
              {typeof value === 'number' && title.includes('Revenue') 
                ? `$${value.toLocaleString()}` 
                : typeof value === 'number' 
                ? value.toLocaleString() 
                : value
              }
            </p>
          </div>
        </div>
        <div className={`flex items-center space-x-1 ${growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {growth >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          <span className="text-sm font-medium">{Math.abs(growth)}%</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-4 lg:p-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        <StatCard
          icon={<Users className="w-5 h-5 text-gray-600" />}
          title="Total Users"
          value={stats.totalUsers.value}
          growth={stats.totalUsers.growth}
        />
        <StatCard
          icon={<Globe className="w-5 h-5 text-gray-600" />}
          title="Active Communities"
          value={stats.activeCommunities.value}
          growth={stats.activeCommunities.growth}
        />
        <StatCard
          icon={<Trophy className="w-5 h-5 text-gray-600" />}
          title="Ongoing Events"
          value={stats.ongoingEvents.value}
          growth={stats.ongoingEvents.growth}
        />
        {/* Total Posts Card with Role Filter */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Trophy className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Posts</p>
                <p className="text-2xl font-bold text-gray-900">
                  {getPostsCount().toLocaleString()}
                </p>
              </div>
            </div>
            <div className={`flex items-center space-x-1 ${stats.totalPosts.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {stats.totalPosts.growth >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              <span className="text-sm font-medium">{Math.abs(stats.totalPosts.growth)}%</span>
            </div>
          </div>
          <select
            value={selectedPostRole}
            onChange={(e) => handlePostRoleChange(e.target.value)}
            className="w-full mt-2 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Roles</option>
            <option value="player">Players ({postsBreakdown['Players'] || 0})</option>
            <option value="coach">Coaches ({postsBreakdown['Coaches'] || 0})</option>
            <option value="admin">Admins ({postsBreakdown['Admins'] || 0})</option>
          </select>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* User Growth Chart */}
        <div className="lg:col-span-2 bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">User Growth</h3>
            <div className="flex space-x-2 overflow-x-auto">
              {['7d', '30d', '1y'].map((period) => (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period)}
                  className={`px-3 py-1 text-xs sm:text-sm rounded-md whitespace-nowrap ${
                    selectedPeriod === period
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
          <div className="h-48 sm:h-56 lg:h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={userGrowth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="month" 
                  stroke="#6b7280"
                  fontSize={12}
                />
                <YAxis 
                  stroke="#6b7280"
                  fontSize={12}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="Champions" 
                  stroke="#eab308" 
                  strokeWidth={3}
                  dot={{ fill: '#eab308', strokeWidth: 2, r: 4 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="Players" 
                  stroke="#1f2937" 
                  strokeWidth={3}
                  dot={{ fill: '#1f2937', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Engagement by Sport */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">Engagement by Sport</h3>
          
          {/* Pie Chart */}
          <div className="h-48 sm:h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={sportEngagement}
                  cx="50%"
                  cy="50%"
                  innerRadius={30}
                  outerRadius={60}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {sportEngagement.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [value.toLocaleString(), 'Users']}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          {/* Custom Legend */}
          <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
            {sportEngagement.map((entry, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-sm flex-shrink-0" 
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-gray-700 truncate">
                  {entry.name}: <span className="font-semibold">{entry.value}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Upcoming Events */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Upcoming Events</h3>
            </div>
            {upcomingEvents.length > 3 && (
              <button 
                onClick={() => navigate('/admin/tournaments')}
                className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                View All
              </button>
            )}
          </div>
          <div className="space-y-3 sm:space-y-4">
            {upcomingEvents.length > 0 ? (
              upcomingEvents.slice(0, 3).map((event: any) => (
                <div key={event.id} className="flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                  <div className="text-xl sm:text-2xl flex-shrink-0">{event.icon}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm sm:text-base text-gray-900 truncate">{event.name}</p>
                    <p className="text-xs sm:text-sm text-gray-600 truncate">{event.date}</p>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">
                      {event.participants || event.location}
                    </p>
                  </div>
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full whitespace-nowrap flex-shrink-0">
                    {event.sport}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No upcoming events</p>
            )}
          </div>
        </div>

        {/* Recent Achievements */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center space-x-2">
              <Award className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Recent Achievements</h3>
            </div>
            {recentAchievements.length > 3 && (
              <button 
                onClick={() => navigate('/admin/achievements')}
                className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                View All
              </button>
            )}
          </div>
          <div className="space-y-3 sm:space-y-4">
            {recentAchievements.length > 0 ? (
              recentAchievements.slice(0, 3).map((achievement: any) => (
                <div key={achievement.id} className="flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                  <div className="text-xl sm:text-2xl flex-shrink-0">{achievement.icon}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm sm:text-base text-gray-900 truncate">{achievement.title}</p>
                    <p className="text-xs sm:text-sm text-gray-600 truncate">{achievement.winner}</p>
                    <p className="text-xs sm:text-sm text-gray-500">{achievement.timeAgo}</p>
                  </div>
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full whitespace-nowrap flex-shrink-0">
                    {achievement.sport}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No recent achievements</p>
            )}
          </div>
        </div>

        {/* Top Communities */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Top Communities</h3>
            </div>
            {topCommunities.length > 3 && (
              <button 
                onClick={() => navigate('/admin/communities')}
                className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                View All
              </button>
            )}
          </div>
          <div className="space-y-3 sm:space-y-4">
            {topCommunities.length > 0 ? (
              topCommunities.slice(0, 3).map((community: any) => (
                <div key={community.id} className="p-2 sm:p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="font-medium text-sm sm:text-base text-gray-900 truncate flex-1">{community.name}</p>
                    <div className="flex items-center space-x-1 text-green-600 flex-shrink-0">
                      <TrendingUp className="w-3 h-3" />
                      <span className="text-xs font-medium whitespace-nowrap">+{community.growth}%</span>
                    </div>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600">{community.members.toLocaleString()} members</p>
                  <p className="text-xs sm:text-sm text-gray-500 truncate">{community.sport}</p>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No communities yet</p>
            )}
          </div>
          {topCommunities.length > 3 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                Showing top 3 of {topCommunities.length} communities
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Community Highlights */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Community Highlights</h3>
          {communityHighlights.length > 1 && (
            <div className="flex space-x-2">
              <button
                onClick={prevHighlight}
                className="p-1 rounded-full hover:bg-gray-100"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={nextHighlight}
                className="p-1 rounded-full hover:bg-gray-100"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          )}
        </div>
        
        {communityHighlights.length > 0 ? (
          <>
            <div className="relative overflow-hidden rounded-lg">
              <img
                src={communityHighlights[currentHighlight].image}
                alt={communityHighlights[currentHighlight].name}
                className="w-full h-40 sm:h-48 object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 sm:p-4">
                <div className="flex items-center space-x-2 mb-1 sm:mb-2">
                  <span className="px-2 py-0.5 sm:py-1 text-xs bg-orange-500 text-white rounded-full whitespace-nowrap">
                    {communityHighlights[currentHighlight].sport}
                  </span>
                </div>
                <h4 className="text-white font-semibold text-base sm:text-lg truncate">
                  {communityHighlights[currentHighlight].name}
                </h4>
                <p className="text-gray-200 text-xs sm:text-sm mb-1 sm:mb-2 line-clamp-2">
                  {communityHighlights[currentHighlight].description}
                </p>
                <p className="text-gray-300 text-xs sm:text-sm mb-2 sm:mb-3">
                  {communityHighlights[currentHighlight].members.toLocaleString()} members
                </p>
                <button className="bg-gray-900 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm hover:bg-gray-800 transition-colors">
                  View Community
                </button>
              </div>
            </div>
        
            <div className="flex justify-center space-x-2 mt-4">
              {communityHighlights.map((_: any, index: number) => (
                <button
                  key={index}
                  onClick={() => setCurrentHighlight(index)}
                  className={`w-2 h-2 rounded-full ${
                    index === currentHighlight ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">No featured communities yet</p>
            <p className="text-sm text-gray-400 mt-2">Feature communities to showcase them here</p>
          </div>
        )}
      </div>

      {/* Bottom Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {bottomStats.map((stat, index) => (
          <div key={index} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{stat.label}</p>
                <p className="text-xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`flex items-center space-x-1 ${
                stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
              }`}>
                {stat.trend === 'up' ? 
                  <TrendingUp className="w-4 h-4" /> : 
                  <TrendingDown className="w-4 h-4" />
                }
                <span className="text-xs font-medium">{stat.change}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Recent Activity</h3>
          {recentActivity.length > 5 && (
            <button 
              onClick={() => navigate('/admin/activity')}
              className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              View All
            </button>
          )}
        </div>
        <div className="space-y-3 sm:space-y-4">
          {recentActivity.length > 0 ? (
            recentActivity.slice(0, 5).map((activity: any) => (
              <div key={activity.id} className="flex items-start space-x-2 sm:space-x-3 p-2 sm:p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-200 rounded-full flex items-center justify-center text-xs font-medium text-gray-600 flex-shrink-0">
                  {activity.avatar}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm text-gray-900 break-words">{activity.text}</p>
                  <div className="flex items-center flex-wrap gap-2 mt-1">
                    <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${activity.badgeColor}`}>
                      {activity.badge}
                    </span>
                    <span className="text-xs text-gray-500 whitespace-nowrap">{activity.timeAgo}</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No recent activity</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;